
import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  getFirestore,
  deleteDoc,
  updateDoc,
  doc,
  onSnapshot,
  where
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { FiBell, FiX, FiTrash, FiEdit, FiSave, FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const NotificationIcon = styled.div`
  position: relative;
  cursor: pointer;
  color: var(--text-main);
  font-size: 1.5rem;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;

  &:hover {
    color: var(--accent);
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ff4081;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
`;

const NotificationPopup = styled(motion.div)`
  position: fixed;
  top: 80px;
  right: 24px;
  width: 380px;
  width: 380px;
  background: var(--bg-card-solid, #1c1c21); /* Use solid theme card background */
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: var(--card-shadow);
  padding: 1.5rem;
  z-index: 2000;
  border: 1px solid var(--border);

  @media (max-width: 768px) {
    right: 10px;
    width: 280px;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const NotificationTitle = styled.h3`
  color: var(--text-main);
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-main);
  cursor: pointer;
  font-size: 1.2rem;

  &:hover {
    color: #ff4081;
  }
`;

const SectionTitle = styled.div`
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 1.5rem 0 0.8rem 0.5rem;
  opacity: 0.8;

  &:first-of-type {
    margin-top: 0.5rem;
  }
`;

const NotificationList = styled.div`
  max-height: ${props => props.expanded ? '500px' : '300px'};
  overflow-y: auto;
  transition: max-height 0.3s ease;
`;

const NotificationItem = styled.div`
  padding: 1.2rem;
  border-radius: 12px;
  margin-bottom: 0.8rem;
  background: var(--bg-surface); /* Use theme surface background */
  border: 1px solid var(--border);
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-2px);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const NotificationMessage = styled.p`
  color: var(--text-main);
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    color: var(--accent);
  }
`;

const NotificationTime = styled.span`
  color: var(--text-muted);
  font-size: 0.7rem;
  display: block;
  margin-top: 0.3rem;
`;

const AdminForm = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
`;

const FormTitle = styled.h4`
  color: var(--accent);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const FormInput = styled.textarea`
  width: 100%;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0.5rem;
  color: var(--text-main);
  margin-bottom: 0.5rem;
  resize: vertical;
  min-height: 80px;
`;

const UrlInput = styled.input`
  width: 100%;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0.5rem;
  color: var(--text-main);
  margin-bottom: 0.5rem;
`;

const SubmitButton = styled.button`
  background: var(--accent);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 20px var(--accent-rgb, 0,255,255)30;
  }
`;

const AdminActions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;

  svg {
    cursor: pointer;
    color: var(--accent);
    font-size: 1rem;

    &:hover {
      color: #ff4081;
    }
  }
`;

const MarkAsReadButton = styled.button`
  background: rgba(var(--accent-rgb), 0.1);
  border: 1px solid rgba(var(--accent-rgb), 0.3);
  color: var(--accent);
  padding: 0.5rem 1rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--accent-rgb), 0.2);
    border-color: var(--accent);
  }
`;

const EnableNotificationsPrompt = styled.div`
  background: var(--bg-surface); /* Use theme surface background */
  border: 1px solid var(--border);
  padding: 0.8rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const PromptMessage = styled.p`
  color: var(--text-main);
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
`;

const PromptButton = styled.button`
  background: var(--accent);
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.8rem;
  margin-right: 0.5rem;

  &:hover {
    opacity: 0.9;
  }
`;

const DismissButton = styled.button`
  background: transparent;
  border: none;
  color: #ff4081;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    text-decoration: underline;
  }
`;

const ToggleExpandButton = styled.button`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  margin-top: 1rem;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-card); /* Different subtle background on hover */
    color: var(--text-main);
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const NotificationComponent = () => {
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [newNotification, setNewNotification] = useState('');
  const [notificationUrl, setNotificationUrl] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editMessage, setEditMessage] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [readNotifications, setReadNotifications] = useState([]);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminStatus = () => {
      if (user && user.email === 'snaped4338@gmail.com') {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    };

    const checkNotificationPermission = () => {
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          setPermissionGranted(true);
        } else if (!localStorage.getItem('notificationPromptDismissed')) {
          setShowPrompt(true);
        }
      }
    };

    const loadReadNotifications = () => {
      const read = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      setReadNotifications(read);
    };

    checkAdminStatus();
    checkNotificationPermission();
    loadReadNotifications();

    if (authLoading) return;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Use onSnapshot for real-time updates and system notifications
    const q = query(
      collection(db, 'notifications'),
      where('timestamp', '>=', sevenDaysAgo),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeNotifications = onSnapshot(q, (snapshot) => {
      const currentFetched = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      })).filter(n =>
        n.recipient === 'all' ||
        n.recipientUid === user?.uid ||
        (!n.recipient && !n.recipientUid) // legacy support
      );

      // We use a ref-like approach to avoid checking against the 'notifications' state 
      // which is in the dependency array. Instead we'll manage the notification trigger carefully.
      setNotifications(prev => {
        if (prev.length > 0 && currentFetched.length > prev.length) {
          const newest = currentFetched[0];
          const isRead = JSON.parse(localStorage.getItem('readNotifications') || '[]').includes(newest.id);
          if (!isRead && 'Notification' in window && Notification.permission === 'granted') {
            new Notification(newest.title || 'New Update from SnapED', {
              body: newest.message,
              icon: '/logo192.png'
            });
          }
        }
        return currentFetched;
      });
    });

    return () => {
      unsubscribeNotifications();
    };
  }, [user, authLoading]);

  useEffect(() => {
    if (notifications.length > 0) {
      const unread = notifications.filter(
        notification => !readNotifications.includes(notification.id)
      ).length;
      setUnreadCount(unread);
    } else {
      setUnreadCount(0);
    }
  }, [notifications, readNotifications]);

  const markAsRead = (id = null) => {
    if (id) {
      const updatedRead = [...readNotifications, id];
      setReadNotifications(updatedRead);
      localStorage.setItem('readNotifications', JSON.stringify(updatedRead));
    } else {
      const allIds = notifications.map(n => n.id);
      setReadNotifications(allIds);
      localStorage.setItem('readNotifications', JSON.stringify(allIds));
    }
  };

  const handleAddNotification = async () => {
    if (!newNotification.trim()) return;
    try {
      await addDoc(collection(db, 'notifications'), {
        message: newNotification,
        url: notificationUrl.trim() || null,
        timestamp: new Date(),
        createdBy: auth.currentUser?.email || 'admin'
      });
      setNewNotification('');
      setNotificationUrl('');

      if (permissionGranted && 'Notification' in window) {
        new Notification('New Update from SnapED', {
          body: newNotification,
          icon: '/path-to-your-logo.png'
        });
      }
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      const updatedRead = readNotifications.filter(notifId => notifId !== id);
      setReadNotifications(updatedRead);
      localStorage.setItem('readNotifications', JSON.stringify(updatedRead));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleEditNotification = (id, message, url) => {
    setEditId(id);
    setEditMessage(message);
    setEditUrl(url || '');
  };

  const handleSaveEdit = async () => {
    try {
      await updateDoc(doc(db, 'notifications', editId), {
        message: editMessage,
        url: editUrl.trim() || null
      });
      setEditId(null);
      setEditMessage('');
      setEditUrl('');
    } catch (error) {
      console.error('Error updating notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.url) {
      if (notification.url.startsWith('http') || notification.url.startsWith('www')) {
        window.open(notification.url, '_blank');
      } else {
        navigate(notification.url);
      }
      setShowPopup(false);
    }
  };

  const requestNotificationPermission = () => {
    if ('Notification' in window) {
      Notification.requestPermission().then(permission => {
        setPermissionGranted(permission === 'granted');
        if (permission === 'granted') {
          setShowPrompt(false);
        }
      });
    }
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
    setPromptDismissed(true);
    localStorage.setItem('notificationPromptDismissed', 'true');
  };

  const formatTime = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <NotificationIcon onClick={() => setShowPopup(!showPopup)}>
        <FiBell />
        {unreadCount > 0 && <NotificationBadge>{unreadCount}</NotificationBadge>}
      </NotificationIcon>

      {showPopup && (
        <NotificationPopup
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          <NotificationHeader>
            <NotificationTitle>Notifications</NotificationTitle>
            <CloseButton onClick={() => setShowPopup(false)}>
              <FiX />
            </CloseButton>
          </NotificationHeader>

          <NotificationList expanded={expanded}>
            {showPrompt && !permissionGranted && (
              <EnableNotificationsPrompt>
                <PromptMessage>
                  Enable browser notifications to get instant updates from SnapED
                </PromptMessage>
                <PromptButton onClick={requestNotificationPermission}>
                  Enable Notifications
                </PromptButton>
                <DismissButton onClick={dismissPrompt}>
                  Dismiss
                </DismissButton>
              </EnableNotificationsPrompt>
            )}

            {notifications.length > 0 ? (
              ['Today', 'Yesterday', 'Earlier this week'].map((section) => {
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);

                const grouped = notifications.filter(n => {
                  const notifDate = new Date(n.timestamp);
                  const d = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

                  if (section === 'Today') return d.getTime() === today.getTime();
                  if (section === 'Yesterday') return d.getTime() === yesterday.getTime();
                  return d.getTime() < yesterday.getTime();
                });

                if (grouped.length === 0) return null;

                return (
                  <div key={section}>
                    <SectionTitle>{section}</SectionTitle>
                    {grouped.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        style={{
                          opacity: readNotifications.includes(notification.id) ? 0.7 : 1
                        }}
                      >
                        {editId === notification.id ? (
                          <>
                            <FormInput
                              value={editMessage}
                              onChange={(e) => setEditMessage(e.target.value)}
                            />
                            <UrlInput
                              type="text"
                              value={editUrl}
                              onChange={(e) => setEditUrl(e.target.value)}
                              placeholder="Enter URL (optional)"
                            />
                            <SubmitButton onClick={handleSaveEdit}>
                              <FiSave /> Save
                            </SubmitButton>
                          </>
                        ) : (
                          <>
                            <NotificationMessage onClick={() => handleNotificationClick(notification)}>
                              {notification.message}
                            </NotificationMessage>
                            {notification.url && (
                              <NotificationTime style={{ color: 'var(--accent)' }}>
                                Link: {notification.url}
                              </NotificationTime>
                            )}
                            <NotificationTime>{formatTime(notification.timestamp)}</NotificationTime>
                            {!readNotifications.includes(notification.id) && (
                              <MarkAsReadButton onClick={() => markAsRead(notification.id)}>
                                <FiCheck /> Mark as read
                              </MarkAsReadButton>
                            )}
                          </>
                        )}

                        {isAdmin && editId !== notification.id && (
                          <AdminActions>
                            <FiEdit onClick={() => handleEditNotification(
                              notification.id,
                              notification.message,
                              notification.url
                            )} />
                            <FiTrash onClick={() => handleDeleteNotification(notification.id)} />
                          </AdminActions>
                        )}
                      </NotificationItem>
                    ))}
                  </div>
                );
              })
            ) : (
              <NotificationItem>
                <NotificationMessage>No notifications yet</NotificationMessage>
              </NotificationItem>
            )}
          </NotificationList>

          {notifications.length > 0 && (
            <ToggleExpandButton onClick={toggleExpand}>
              {expanded ? (
                <>
                  <FiChevronUp /> Show Less
                </>
              ) : (
                <>
                  <FiChevronDown /> Show More
                </>
              )}
            </ToggleExpandButton>
          )}

          {unreadCount > 0 && (
            <MarkAsReadButton
              onClick={() => markAsRead()}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <FiCheck /> Mark all as read
            </MarkAsReadButton>
          )}

          {isAdmin && (
            <AdminForm>
              <FormTitle>Add New Notification</FormTitle>
              <FormInput
                value={newNotification}
                onChange={(e) => setNewNotification(e.target.value)}
                placeholder="Enter notification message..."
              />
              <UrlInput
                type="text"
                value={notificationUrl}
                onChange={(e) => setNotificationUrl(e.target.value)}
                placeholder="Enter URL to navigate to (optional)"
              />
              <SubmitButton onClick={handleAddNotification}>
                Post Notification
              </SubmitButton>
            </AdminForm>
          )}
        </NotificationPopup>
      )}
    </>
  );
};

export default NotificationComponent;